import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import {
  Button, Col, Row, Select,
  Switch,
} from 'antd';
import {
  LeftOutlined,
} from '@ant-design/icons';
import {
  useHistory,
  useParams,
} from 'react-router-dom';

import { getObsWithQartod } from '../../../../services/dataset.service';
import ParameterCard from './ParameterCard';
import QartodCard from './QartodCard';
import ParameterMessage from '../dashboard/ParameterMessage';
import ParameterFilters from '../../../../components/parameter/ParameterFilters';
import useParameterGroups from '../../../../config/useParameterGroups';
import { getDisplayName, getConnectionStatus } from '../utils';
import { getParameterObsLatestDate } from '../../../../utils';

const ParametersTab = (props) => {
  const {
    obsDatasetSummary,
    parameterConfigurationsResult,
    registeredParametersResult,
    obsDataV2,
    onObsDateRangeChange,
    obsLatestData,
    isLoading,
  } = props;
  const { obsDatasetId: obsDatasetIdParam, parameterId: parameterIdParam } = useParams();
  const obsDatasetId = parseInt(obsDatasetIdParam, 10);

  const latestTimestamp = obsDataV2?.timestamps?.length > 1
    ? obsDataV2.timestamps[obsDataV2.timestamps.length - 1]
    : undefined;

  const parameterId = parseInt(parameterIdParam, 10);
  const history = useHistory();

  const [timestamp, setTimestamp] = useState(latestTimestamp);
  const [queryParameterId, setQueryParameterId] = useState(parameterId);
  const [sortOrder, setSortOrder] = useState('asc');
  const [hideOffline, setHideOffline] = useState(true);

  const { parameterGroups } = useParameterGroups();
  const [selectedParameterCategory, setSelectedParameterCategory] = useState('');
  // Derive all parameters for this platform
  const allPlatformParameters = useMemo(() => {
    const platformId = obsDatasetSummary?.obs_dataset_platform_assignment?.platform?.platform_id;
    return registeredParametersResult?.filter((param) => param.platform_id === platformId) || [];
  }, [registeredParametersResult, obsDatasetSummary]);

  // Derive filtered parameters
  const platformParameters = useMemo(() => {
    let filtered = allPlatformParameters;

    if (hideOffline) {
      filtered = filtered.filter((param) => {
        const parameterLatestTimestamp = getParameterObsLatestDate(obsLatestData, obsDatasetId, param.parameter_id);
        const status = getConnectionStatus(parameterLatestTimestamp);
        return status.status === 'online';
      });
    }

    if (selectedParameterCategory) {
      filtered = allPlatformParameters.filter((param) => {
        const group = parameterGroups.find((g) => g.parameter_standard_names?.includes(param.standard_name));
        return group?.group_name === selectedParameterCategory;
      });
    }

    const sorted = [...filtered].sort((a, b) => {
      const aName = getDisplayName(parameterConfigurationsResult, a);
      const bName = getDisplayName(parameterConfigurationsResult, b);
      return aName.localeCompare(bName);
    });

    if (sortOrder === 'desc') {
      sorted.reverse();
    }

    return sorted;
  }, [
    allPlatformParameters,
    selectedParameterCategory,
    parameterGroups,
    parameterConfigurationsResult,
    sortOrder,
    hideOffline,
  ]);

  // Derive categories without using state
  const parameterCategories = useMemo(() => {
    const categories = new Set();

    allPlatformParameters.forEach((param) => {
      const group = parameterGroups.find((g) => g.parameter_standard_names?.includes(param.standard_name));
      if (group?.group_name) categories.add(group.group_name);
    });

    return Array.from(categories);
  }, [allPlatformParameters, parameterGroups]);

  const {
    data: obsQartodData,
    refetch: refetchObsQartod,
    isLoading: obsQartodIsLoading,
  } = useQuery(
    ['obs-with-qartod', timestamp, queryParameterId],
    () => getObsWithQartod(timestamp, queryParameterId),
    {
      enabled: !!timestamp && !Number.isNaN(queryParameterId),
    },
  );

  const getQartodResults = useCallback((ts) => {
    setTimestamp(ts);
    setQueryParameterId(parameterId);
  }, [parameterId, setTimestamp, setQueryParameterId]);

  useEffect(() => {
    if (timestamp && parameterId) {
      refetchObsQartod();
    }
  }, [timestamp, queryParameterId]);

  useEffect(() => {
    setTimestamp(latestTimestamp);
  }, [latestTimestamp]);

  const onChangeSortOrder = (value) => {
    setSortOrder(value);
  };

  const onChangeHideOffline = (checked) => {
    setHideOffline(checked);
  };

  const onBackClick = () => {
    onObsDateRangeChange(1);
    history.push(`/data-console/manage/${obsDatasetIdParam}/parameters`);
  };

  if (parameterId) {
    const parameter = platformParameters?.find((param) => param.parameter_id === parameterId);

    if (!parameter) {
      return null;
    }

    return (
      <div>
        <Button
          style={{ marginBottom: 16 }}
          className="action-button"
          icon={<LeftOutlined />}
          onClick={onBackClick}
        >
          Back
        </Button>
        <Row
          gutter={[16, 16]}
        >
          <Col
            xs={24}
            lg={17}
          >
            <ParameterCard
              parameter={parameter}
              parameterConfigurationsResult={parameterConfigurationsResult}
              obsDataV2={obsDataV2}
              getQartodResults={getQartodResults}
              onObsDateRangeChange={onObsDateRangeChange}
              obsLatestData={obsLatestData}
              isLoading={isLoading}
              selectedTimestamp={timestamp}
            />
          </Col>
          <Col
            xs={24}
            lg={7}
          >
            <QartodCard
              isLoading={obsQartodIsLoading}
              data={obsQartodData}
              timestamp={timestamp}
              parameter={parameter}
              obsDataV2={obsDataV2}
              parameterConfigurationsResult={parameterConfigurationsResult}
            />
            <ParameterMessage
              parameterId={parameterId}
            />
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div>
      <Row
        justify="space-between"
        align="middle"
      >
        <Col>
          <ParameterFilters
            categorySelected={selectedParameterCategory}
            onCategoriesChange={setSelectedParameterCategory}
            categoriesPresent={parameterCategories}
          />
        </Col>
        <Col>
          <Row
            gutter={12}
            align="middle"
            style={{
              marginBottom: 10,
            }}
          >
            <Col>
              <Row
                gutter={8}
                align="middle"
              >
                <Col>
                  <label
                    style={{
                      color: '#A7B7C0',
                    }}
                  >
                    Sort:
                  </label>
                </Col>
                <Col>
                  <Select
                    defaultValue={sortOrder}
                    onChange={onChangeSortOrder}
                    getPopupContainer={() => document.body} // keeps dropdown from showing up behind graphs or modals
                    style={{
                      width: 150,
                    }}
                    options={[
                      {
                        value: 'asc',
                        label: 'A-Z',
                      },
                      {
                        value: 'desc',
                        label: 'Z-A',
                      },
                    ]}
                  />
                </Col>
              </Row>
            </Col>
            <Col>
              <Row
                gutter={8}
                align="middle"
              >
                <Col>
                  <label
                    style={{
                      color: '#A7B7C0',
                    }}
                  >
                    Hide offline:
                  </label>
                </Col>
                <Col>
                  <Switch
                    checked={hideOffline}
                    onChange={onChangeHideOffline}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        {platformParameters?.map((param) => (
          <Col
            key={param.parameter_id}
            xs={24}
            md={12}
            xxl={8}
          >
            <ParameterCard
              parameter={param}
              parameterConfigurationsResult={parameterConfigurationsResult}
              obsDataV2={obsDataV2}
              handleChartClick={undefined}
              obsLatestData={obsLatestData}
            />
          </Col>
        ))}
      </Row>
    </div>
  );
};

ParametersTab.propTypes = {
  obsDatasetSummary: PropTypes.object,
  parameterConfigurationsResult: PropTypes.arrayOf(PropTypes.object),
  registeredParametersResult: PropTypes.arrayOf(PropTypes.object),
  obsDataV2: PropTypes.array,
  onObsDateRangeChange: PropTypes.func,
  obsLatestData: PropTypes.array,
  isLoading: PropTypes.bool,
};

ParametersTab.defaultProps = {
  obsDatasetSummary: {},
  parameterConfigurationsResult: [],
  registeredParametersResult: [],
  obsDataV2: [],
  onObsDateRangeChange: undefined,
  obsLatestData: [],
  isLoading: false,
};

export default ParametersTab;
